/* eslint no-console: 0 */
import {normalizeText} from "./normalize_text";
import {searchInDocument} from "./search_in_document";

export interface ProgressiveSearchResult {
  ranges: Word.Range[];
  wordsUsed: number;
}

const MAX_CANDIDATES = 10;

export async function searchWithProgressiveExpansion(
  context: Word.RequestContext,
  anchorText: string,
  fromEnd: boolean,
  startWords = 1,
  maxWords = 30,
  stepSize = 1,
): Promise<ProgressiveSearchResult | null> {
  const words = normalizeText(anchorText)
    .split(/\s+/)
    .filter(w => w.length > 0);

  if (words.length === 0) {
    return null;
  }

  let bestResults: Word.Range[] = [];
  let bestWordsUsed = 0;

  if (words.length <= startWords) {
    const searchText = words.join(" ");
    const results = await searchInDocument(context, searchText);
    if (results.length > MAX_CANDIDATES) {
      console.log(
        `progressiveExpansion: Anchor "${searchText.substring(
          0,
          50,
        )}" matched ${
          results.length
        } times (> ${MAX_CANDIDATES}), too ambiguous to use`,
      );
      return null;
    }
    if (results.length >= 1) {
      return {ranges: results, wordsUsed: words.length};
    }
    return null;
  }

  for (
    let n = startWords;
    n <= Math.min(maxWords, words.length);
    n += stepSize
  ) {
    const searchWords = fromEnd ? words.slice(-n) : words.slice(0, n);
    const searchText = searchWords.join(" ");

    const results = (await searchInDocument(context, searchText)) || [];

    if (results.length === 1) {
      console.log(
        `progressiveExpansion: Found unique match with ${n} words: "${searchText.substring(
          0,
          50,
        )}..."`,
      );
      return {ranges: results, wordsUsed: n};
    }

    if (results.length > 1 && results.length <= MAX_CANDIDATES) {
      if (bestResults.length === 0 || results.length < bestResults.length) {
        bestResults = results;
        bestWordsUsed = n;
      }
    }

    if (results.length === 0 && bestResults.length > 0) {
      console.log(
        `progressiveExpansion: Hit paragraph boundary at ${n} words, returning ${bestResults.length} candidates from ${bestWordsUsed} words`,
      );
      return {ranges: bestResults, wordsUsed: bestWordsUsed};
    }

    if (results.length === 0 && n >= maxWords) {
      console.log(
        `progressiveExpansion: No results with ${n} words, returning null`,
      );
      return null;
    }
  }

  if (bestResults.length > 0) {
    console.log(
      `progressiveExpansion: No unique match, returning ${bestResults.length} candidates from ${bestWordsUsed} words for pairing`,
    );
    return {ranges: bestResults, wordsUsed: bestWordsUsed};
  }

  console.log(
    `progressiveExpansion: Could not find any matches within ${maxWords} words`,
  );
  return null;
}
